"use client"

import { useQuery } from "@tanstack/react-query"
import { clabe as ClabeValidator } from "clabe-validator"
import { jwtDecode } from "jwt-decode"
import { ArrowRight, TrendingUp, Zap } from "lucide-react"
import type React from "react"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import useLocalStorageArray from "../hooks/useLocalStorageArray"
import { api } from "../services/api"
import "./SellTransactionForm.css"

interface TokenAsset {
  id: number
  name: string
  symbol: string
  logo_url: string
  is_withdrawable: boolean
}

const SellTransactionForm: React.FC = () => {
  const [amount, setAmount] = useState("")
  const [networks, setNetworks] = useState<any[]>([])
  const [selectedNetwork, setSelectedNetwork] = useState<any | null>(null)
  // const [tokens, setTokens] = useState([])
  const [selectedToken, setSelectedToken] = useState<any | null>(null)
  const [clabe, setClabe] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [quote, setQuote] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)

  const [prevClabes, addPrevClabe] = useLocalStorageArray("sellForm-clabe", [])
  const [prevFirstNames, addPrevFirstName] = useLocalStorageArray("sellForm-firstName", [])
  const [prevLastNames, addPrevLastName] = useLocalStorageArray("sellForm-lastName", [])

  const [clabeError, setClabeError] = useState<string | null>(null)
  const [amountError, setAmountError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [transactionLimitId, setTransactionLimitId] = useState<number | null>(null)

  const navigate = useNavigate()

  // Helper function to format currency with commas
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Retrieve the auth token and decode it to get transactionLimitId
  useEffect(() => {
    const token = localStorage.getItem("authToken")
    if (token) {
      try {
        const decoded: any = jwtDecode(token)
        setTransactionLimitId(decoded.transactionLimit)
      } catch (error: any) {
        console.error("Error decoding auth token:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to decode authorization token. Please try again later."
        setError(errorMessage)
      }
    }
  }, [])

  // Fetch networks on component mount
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const response = await api.get(`/networks`)
        const filteredNetworks = response.data.filter((network: any) => network.can_withdraw)
        setNetworks(filteredNetworks)
        setError(null)
      } catch (error: any) {
        console.error("Error fetching networks:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to fetch networks. Please try again later."
        setError(errorMessage)
      }
    }
    fetchNetworks()
  }, [])

  // Fetch all assets for all networks in parallel
  const {
    data: allRampAssets = {},
    // isLoading: loadingRampAssets,
    // error: rampAssetsError,
  } = useQuery<Record<number, TokenAsset[]>>({
    queryKey: ["allRampAssets", networks.map((n) => n.id)],
    queryFn: async () => {
      if (!networks.length) return {}

      // Fetch payment assets for all networks in parallel
      const promises = networks.map((network) =>
        api
          .get(`/networks/${network.id}/assets`)
          .then((response) => {
            const withdrawableAssets = response.data.filter(
              (asset: TokenAsset) => asset.is_withdrawable
            )
            return { networkId: network.id, assets: withdrawableAssets }
          })
          .catch((error) => {
            console.warn(`Failed to fetch assets for network ${network.id}:`, error)
            return { networkId: network.id, assets: [] }
          }),
      )

      const results = await Promise.all(promises)

      // Convert array to object for easy lookup
      return results.reduce(
        (acc, { networkId, assets }) => {
          if (assets.length > 0) {
            acc[networkId] = assets
          }
          // console.log(acc)
          return acc
        },
        {} as Record<number, TokenAsset[]>,
      )
    },
    enabled: networks.length > 0,
    staleTime: 1000 * 60 * 10,
  })

  // Get tokens for the selected network from our pre-fetched data
  const tokens = selectedNetwork ? allRampAssets[selectedNetwork.id] || [] : []

  // Fetch tokens when network is selected
  // useEffect(() => {
  //   if (selectedNetwork) {
  //     const fetchTokens = async () => {
  //       try {
  //         const response = await api.get(`/networks/${selectedNetwork.id}/assets`)
  //         const filteredTokens = response.data.filter((token: any) => token.is_withdrawable)
  //         setTokens(filteredTokens)
  //         setError(null)
  //       } catch (error: any) {
  //         console.error("Error fetching tokens:", error)
  //         const errorMessage =
  //           error.response && error.response.data && error.response.data.message
  //             ? error.response.data.message
  //             : "Failed to fetch tokens. Please try again later."
  //         setError(errorMessage)
  //       }
  //     }
  //     fetchTokens()
  //   }
  // }, [selectedNetwork])

  // Reset amount and quote when network or token changes
  useEffect(() => {
    setAmount("")
    setQuote(null)
    setAmountError(null)
  }, [selectedNetwork, selectedToken])

  // CLABE Validation
  const validateClabe = (clabeValue: string) => {
    const clabeCheck = ClabeValidator.validate(clabeValue)
    return clabeCheck.ok
  }

  const handleClabeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setClabe(value)

    if (value && !validateClabe(value)) {
      setClabeError("Invalid CLABE.")
    } else {
      setClabeError(null)
      if (value && validateClabe(value)) {
        addPrevClabe(value)
      }
    }
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAmount(value)

    const numericValue = Number.parseFloat(value)

    let min = 0
    let max = Number.POSITIVE_INFINITY

    let amountMaxLimit = 100
    switch (transactionLimitId) {
      default:
      case 1:
        amountMaxLimit = 100
        break
      case 2:
      case 3:
        amountMaxLimit = 2500
        break
      case 4:
        amountMaxLimit = 5000
        break
      case 5:
        amountMaxLimit = 10000
        break
      case 6:
        amountMaxLimit = 25000
        break
    }

    if (selectedToken) {
      switch (selectedToken.symbol) {
        case "BTC":
          min = 0.0001
          max = 0.01
          break
        case "HYPE":
          min = 0.01
          max = 5
          break
        case "ETH":
          min = 0.0025
          max = 0.1
          break
        case "SOL":
          min = 0.02
          max = 3
          break
        case "USDC":
        case "USDT":
          min = 1
          max = 2500
          break
        default:
          min = 1
          max = 10000
          break
      }
    }

    if (isNaN(numericValue) || numericValue < min || numericValue > max) {
      setAmountError(`Amount must be between ${min} and ${max} for ${selectedToken?.symbol || "the selected token"}.`)
    } else {
      setAmountError(null)

      if (selectedToken && selectedNetwork) {
        fetchQuote(numericValue, amountMaxLimit)
      }
    }
  }

  const fetchQuote = useCallback(
    (numericValue: number, amountMaxLimit: number) => {
      if (selectedToken && selectedNetwork) {
        setLoading(true)
        api
          .post("/quote", {
            fromChain: selectedNetwork.name,
            fromToken: selectedToken.symbol,
            amount: numericValue,
            direction: "sell",
          })
          .then((response) => {
            setLoading(false)
            const quoteData = response.data.data
            const mxnValue = Number.parseFloat(quoteData.quoteAmount)
            if (mxnValue > amountMaxLimit) {
              setError(
                `Transaction amount exceeds your available limit of ${formatCurrency(amountMaxLimit)} MXN. Please adjust the amount and try again.`,
              )
              setQuote(null)
            } else {
              setQuote(quoteData)
              setError(null)
            }
          })
          .catch((error) => {
            console.error("Error fetching quote:", error)
            setLoading(false)
            const errorMessage =
              error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : "Failed to fetch quote. Please try again later."
            setError(errorMessage)
          })
      }
    },
    [selectedNetwork, selectedToken],
  )

  // Comprehensive validation function
  const isFormValid = () => {
    // Check if all required fields are present
    if (!firstName || !lastName || !clabe || !selectedNetwork || !selectedToken || !amount) {
      return false
    }

    // Check if there are any validation errors
    if (clabeError || amountError) {
      return false
    }

    // Check if quote is available and not loading
    if (!quote || loading) {
      return false
    }

    // Check if amount is valid number
    const numericValue = Number.parseFloat(amount)
    if (isNaN(numericValue) || numericValue <= 0) {
      return false
    }

    // Check if CLABE is valid
    if (!validateClabe(clabe)) {
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const token = localStorage.getItem("authToken")
    if (token) {
      try {
        const decoded: any = jwtDecode(token)

        if (decoded.kycStatus !== "accepted") {
          navigate("/kyc")
          return
        }
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`
      } catch (error: any) {
        console.error("Error decoding token:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Login expired."
        setError(errorMessage)
        navigate("/auth")
        return
      }
    } else {
      navigate("/auth")
      return
    }

    if (isFormValid()) {
      addPrevFirstName(firstName)
      addPrevLastName(lastName)
      try {
        const response = await api.post("/ramp/sell", {
          amount,
          token: selectedToken.symbol,
          network: selectedNetwork.name,
          first_name: firstName,
          last_name: lastName,
          to_offchain: clabe,
        })

        navigate(`/transactions/${response.data.transaction.txId}`)
      } catch (error: any) {
        console.error("Error submitting transaction:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to submit transaction. Please try again later."
        setError(errorMessage)
      }
    }
  }

  return (
    <div className="sell-form-container">
      <div className="sell-form-card">
        <div className="sell-form-header">
          <h2>
            <TrendingUp className="header-icon" />
            Vende crypto por MXN
          </h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="sell-form">
          {/* Personal Information Section */}
          <div className="form-section">
            {/* <h3>
              <User className="section-icon" />
              Información personal
            </h3> */}
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nombres *</label>
                <input
                  type="text"
                  list="first-name-suggestions"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Ingresa tu nombre"
                  className="form-input"
                  required
                />
                <datalist id="first-name-suggestions">
                  {prevFirstNames.map((val, idx) => (
                    <option key={idx} value={val} />
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label className="form-label">Apellidos *</label>
                <input
                  type="text"
                  list="last-name-suggestions"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Ingresa tus apellidos"
                  className="form-input"
                  required
                />
                <datalist id="last-name-suggestions">
                  {prevLastNames.map((val, idx) => (
                    <option key={idx} value={val} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>

          {/* Bank Information Section */}
          <div className="form-section">
            {/* <h3>
              <CreditCard className="section-icon" />
              Información bancaria
            </h3> */}
            <div className="form-group">
              <label className="form-label">CLABE (para recibir MXN) *</label>
              <input
                type="text"
                list="clabe-suggestions"
                value={clabe}
                onChange={handleClabeChange}
                placeholder="Ingresa tu CLABE de 18 dígitos"
                className="form-input"
                maxLength={18}
                required
              />
              <datalist id="clabe-suggestions">
                {prevClabes.map((val, idx) => (
                  <option key={idx} value={val} />
                ))}
              </datalist>
              {clabeError && <span className="field-error">{clabeError}</span>}
            </div>
          </div>

          {/* Network Selection */}
          <div className="form-section">
            <h3>Red de envío *</h3>
            <div className="networks-grid">
              {networks
              .filter((network: any) => allRampAssets.hasOwnProperty(network.id))
              .map((network: any) => (
                <div
                  key={network.id}
                  className={`network-card ${selectedNetwork?.id === network.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedNetwork(network)
                    setSelectedToken(null) // Reset token when network changes
                  }}
                >
                  <div className="network-logo">
                    <img
                      src={network.logo_url || "/placeholder.svg"}
                      alt={network.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = "/placeholder.svg"
                      }}
                    />
                  </div>
                  <p className="network-name">{network.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Token Selection */}
          {selectedNetwork && (
            <div className="form-section">
              <h3>Token *</h3>
              <div className="tokens-grid">
                {tokens
                // .filter((token: any) => token.is_withdrawable)
                .map((token: any) => (
                  <div
                    key={token.id}
                    className={`token-card ${selectedToken?.id === token.id ? "selected" : ""}`}
                    onClick={() => setSelectedToken(token)}
                  >
                    <div className="token-logo">
                      <img
                        src={token.logo_url || "/placeholder.svg"}
                        alt={token.symbol}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = "/placeholder.svg"
                        }}
                      />
                    </div>
                    <div className="token-info">
                      <div className="token-symbol">{token.symbol}</div>
                      {/* <div className="token-name">{token.name}</div> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Amount Section */}
          <div className="form-section">
            <h3>Cantidad a vender *</h3>
            <div className="form-group">
              <input
                type="number"
                value={amount}
                onChange={handleAmountChange}
                placeholder={`Ingresa cantidad en ${selectedToken?.symbol || "token"}`}
                className="form-input"
                step="any"
                required
              />
              {amountError && <span className="field-error">{amountError}</span>}
            </div>
          </div>

          {/* Quote Section */}
          {selectedNetwork && selectedToken && (
            <div className="quote-section">
              <div className="quote-card">
                <div className="quote-header">
                  <Zap className="quote-icon" />
                  <h4>Cotización en tiempo real</h4>
                </div>
                <div className="quote-content">
                  {loading ? (
                    <div className="quote-loading">
                      <div className="loading-spinner"></div>
                      <span>Calculando mejor precio...</span>
                    </div>
                  ) : quote ? (
                    <div className="quote-details">
                      <div className="quote-amount">{quote.quoteAmount} MXN</div>
                      <div className="quote-rate">@ {quote.quoteRate} MXN</div>
                      <div className="quote-network">por {selectedToken?.symbol}</div>
                    </div>
                  ) : amount ? (
                    <div className="quote-placeholder">Ingresa una cantidad válida para ver la cotización</div>
                  ) : (
                    <div className="quote-placeholder">—</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className={`submit-button ${isFormValid() ? "enabled" : "disabled"}`}
            disabled={!isFormValid()}
          >
            {loading ? (
              <>
                <div className="loading-spinner small"></div>
                Procesando...
              </>
            ) : (
              <>
                Confirmar venta
                <ArrowRight className="btn-icon" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default SellTransactionForm
